import { useState, useMemo } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Container, Button, Skeleton, SkeletonText } from "@/components/atoms";
import { Breadcrumb, FdaDisclaimer, EmptyState } from "@/components/molecules";
import { ImageGallery } from "@/components/pdp/image-gallery";
import { ProductInfo, type BuyState } from "@/components/pdp/product-info";
import { WhyFormula } from "@/components/pdp/why-formula";
import { ClinicalNote } from "@/components/pdp/clinical-note";
import { Dosing } from "@/components/pdp/dosing";
import { LabResults } from "@/components/pdp/lab-results";
import { ReviewsSection } from "@/components/pdp/reviews-section";
import { RelatedProducts } from "@/components/pdp/related-products";
import { SubscriptionBlock } from "@/components/pdp/subscription-block";
import { PdpFaq } from "@/components/pdp/pdp-faq";
import { MobileStickyCta } from "@/components/pdp/mobile-sticky-cta";
import { useProduct } from "@/hooks/use-products";
import { useCartStore } from "@/stores/cart-store";
import { useUIStore } from "@/stores/ui-store";
import {
  defaultVariant,
  variantPrice,
  subscribePrice,
  primaryImage,
} from "@/lib/product-helpers";
import { canonical, productJsonLd, breadcrumbJsonLd } from "@/lib/seo";
import { formatPrice } from "@/lib/format";
import { track } from "@/lib/analytics";
import { Seo } from "@/components/seo";

export default function ProductDetail() {
  const { slug = "" } = useParams();
  const navigate = useNavigate();
  const { data: product, isLoading, isError } = useProduct(slug);
  const add = useCartStore((s) => s.add);
  const openCartDrawer = useUIStore((s) => s.openCartDrawer);

  const [variant, setVariant] = useState(() =>
    product ? defaultVariant(product) : undefined,
  );
  const [isSubscription, setIsSubscription] = useState(false);
  const [quantity, setQuantity] = useState(1);

  // Resolve the active variant once the product loads.
  const activeVariant = useMemo(
    () => variant ?? (product ? defaultVariant(product) : undefined),
    [variant, product],
  );

  /* CLS — this returned a centred 40vh spinner and then swapped in several viewport-heights of
     content. Measured live at 390x844 before this change: CLS 0.4579 on /products/cbd-pure-1200mg
     against a 0.1 "good" threshold. A skeleton shaped like the real page costs one render and
     removes the jump; a spinner cannot, because its height has nothing to do with what replaces it. */
  if (isLoading) {
    return (
      <>
        <Container className="py-6">
          <Skeleton className="h-4 w-56" />
        </Container>
        <Container className="grid gap-10 pb-12 md:grid-cols-2">
          <Skeleton className="aspect-square w-full rounded-card" />
          <div className="space-y-4">
            <Skeleton className="h-9 w-3/4" />
            <Skeleton className="h-5 w-32" />
            <SkeletonText lines={3} />
            <Skeleton className="h-12 w-full rounded-pill" />
            <Skeleton className="h-12 w-full rounded-pill" />
          </div>
        </Container>
      </>
    );
  }
  if (isError || !product || !activeVariant) {
    return (
      <Container className="py-16">
        <EmptyState
          title="Product not found"
          description="That product may have moved or sold out."
          action={<Button to="/shop">Browse all products</Button>}
        />
      </Container>
    );
  }

  const isPlaceholderPhoto = product.slug === "cbd-opioid-recovery-600mg";

  function doAdd(asSubscription: boolean, frequencyDays?: number) {
    const unit = asSubscription
      ? subscribePrice(product!, activeVariant!)
      : variantPrice(product!, activeVariant!);
    add({
      productId: product!.id,
      variantId: activeVariant!.id,
      productName: product!.name,
      variantLabel: activeVariant!.label,
      slug: product!.slug,
      unitPrice: unit,
      quantity,
      isSubscription: asSubscription,
      frequencyDays,
      imageUrl: primaryImage(product!),
    });
    track("add_to_cart", {
      item_id: product!.id,
      value: unit / 100,
      subscription: asSubscription,
    });
    openCartDrawer();
    navigate("/cart");
  }

  const buy: BuyState = {
    variant: activeVariant,
    isSubscription,
    quantity,
    setVariant,
    setIsSubscription,
    setQuantity,
    addToCart: () => doAdd(isSubscription),
  };

  const priceDollars = (variantPrice(product, activeVariant) / 100).toFixed(2);

  // When a product ships one image per variant (1:1, same order — e.g. the
  // ointment's three scents), the gallery follows the selected option. For
  // shared-photo products (one image, multiple strengths) it stays put.
  const variantImageIndex =
    product.images.length === product.variants.length
      ? product.variants.findIndex((v) => v.id === activeVariant.id)
      : undefined;

  return (
    <>
      <Seo
        title={product.name}
        path={`/products/${product.slug}`}
        description={product.shortDescription}
        type="product"
        image={product.images?.[0] ? canonical(product.images[0]) : undefined}
      >
        <script type="application/ld+json">
          {JSON.stringify(productJsonLd(product, priceDollars))}
        </script>
        <script type="application/ld+json">
          {JSON.stringify(
            breadcrumbJsonLd([
              { name: "Home", path: "/" },
              { name: "Shop", path: "/shop" },
              { name: product.name, path: `/products/${product.slug}` },
            ]),
          )}
        </script>
      </Seo>

      <Container className="py-6">
        <Breadcrumb
          items={[
            { label: "Home", to: "/" },
            { label: "Shop", to: "/shop" },
            { label: product.name },
          ]}
        />
      </Container>

      <Container className="grid gap-10 pb-12 md:grid-cols-2">
        <div>
          <ImageGallery
            images={product.images}
            name={product.name}
            selectedIndex={variantImageIndex}
          />
          {isPlaceholderPhoto && (
            <p className="mt-3 flex items-center gap-2 rounded-soft bg-calm-orange/10 px-3 py-2 text-xs text-calm-orange-ink">
              <AlertTriangle size={14} /> Product photography in progress —
              placeholder shown.
            </p>
          )}
        </div>
        <ProductInfo product={product} buy={buy} />
      </Container>

      <WhyFormula product={product} />
      <ClinicalNote product={product} />
      <Dosing product={product} />
      <LabResults product={product} />
      <SubscriptionBlock
        product={product}
        variant={activeVariant}
        onStart={(freq) => doAdd(true, freq)}
      />
      <ReviewsSection product={product} />
      <PdpFaq product={product} />
      <RelatedProducts product={product} />

      <Container className="pb-28 sm:pb-12">
        <FdaDisclaimer />
        <p className="mt-2 text-xs text-night-blue/70">
          Subscribe &amp; Save price:{" "}
          {formatPrice(subscribePrice(product, activeVariant))}/shipment.
        </p>
      </Container>

      <MobileStickyCta product={product} buy={buy} />
    </>
  );
}
